import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useFormPopupContext } from '../contexts/FormPopupContext';

// Responsive hero image sets - moved outside component to prevent recreating on every render
const heroImageSets = {
  desktop: [
    '/images/parallaxcinema-hero-one.png',
    '/images/parallaxcinema-hero-two.png',
    '/images/parallaxcinema-hero-three.png',
    '/images/parallaxcinema-hero-four.png'
  ],
  mobile: [
    '/images/parallaxcinema-flight-sim-mobile.png',
    '/images/parallaxcinema-golf-sim-mobile.png',
    '/images/parallaxcinema-flight-sim-mobile.png', // Repeat for 4 images
    '/images/parallaxcinema-golf-sim-mobile.png'    // Repeat for 4 images
  ]
};

const Experience: React.FC = () => {
  // Mobile detection hook
  const [isMobile, setIsMobile] = useState(false);
  
  // Global popup context
  const { openFormPopup } = useFormPopupContext();
  
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    // Check on mount
    checkMobile();
    
    // Add resize listener
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Get current image set based on device type
  const currentImageSet = isMobile ? heroImageSets.mobile : heroImageSets.desktop;
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Auto-advance carousel every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % currentImageSet.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [currentImageSet.length]);

  // Preload images for better performance
  useEffect(() => {
    // Preload current device images
    currentImageSet.forEach(src => {
      const img = new Image();
      img.src = src;
    });
    
    // Preload the other set for faster switching
    const otherImageSet = isMobile ? heroImageSets.desktop : heroImageSets.mobile;
    otherImageSet.forEach(src => {
      const img = new Image();
      img.src = src;
    });
  }, [currentImageSet, isMobile]);

  // Reset image index when switching between mobile/desktop
  useEffect(() => {
    setCurrentImageIndex(0);
  }, [isMobile]);

  return (
    <div className="min-h-screen">
      {/* LUXURY EXPERIENCES HERO SECTION */}
      <section className="relative h-screen overflow-hidden">
        {/* Dynamic Background Carousel */}
        <div className="absolute inset-0">
          <AnimatePresence mode="wait">
            <motion.div 
              className="w-full h-full absolute inset-0"
              key={`${isMobile ? 'mobile' : 'desktop'}-${currentImageIndex}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ 
                duration: 1.2, 
                ease: "easeInOut"
              }}
            >
              <div 
                className="w-full h-full hero-image"
                style={{
                  backgroundImage: `url(${currentImageSet[currentImageIndex]})`,
                  backgroundSize: 'cover',
                  backgroundPosition: isMobile ? 'center center' : 'center',
                  backgroundRepeat: 'no-repeat'
                }}
              />
            </motion.div>
          </AnimatePresence>
          {/* Smart mobile overlay - lighter and more strategic */}
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/20 to-transparent sm:bg-black/40 z-10"></div>
          
          {/* Additional text-area specific overlay for mobile */}
          <div className="absolute inset-0 bg-gradient-radial-mobile sm:hidden z-10"></div>
          
          {/* Image Navigation Dots */}
          <div className="absolute bottom-6 sm:bottom-8 left-1/2 transform -translate-x-1/2 z-20">
            <div className="flex space-x-2 sm:space-x-3">
              {currentImageSet.map((_, index) => (
                <button
                  key={`${isMobile ? 'mobile' : 'desktop'}-dot-${index}`}
                  onClick={() => setCurrentImageIndex(index)}
                  className={`w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full transition-all duration-300 ${
                    index === currentImageIndex
                      ? 'bg-highlight scale-125' 
                      : 'bg-white/40 hover:bg-white/60'
                  }`}
                  aria-label={`View image ${index + 1}`}
                />
              ))}
            </div>
          </div>
          
          {/* Navigation Arrows */}
          <button
            onClick={() => setCurrentImageIndex((prev) => (prev - 1 + currentImageSet.length) % currentImageSet.length)}
            className="absolute left-3 sm:left-6 top-1/2 transform -translate-y-1/2 z-20 glass-effect rounded-full p-2 sm:p-3 hover:scale-110 transition-all duration-300 group"
            aria-label="Previous image"
          >
            <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white group-hover:text-highlight" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          
          <button
            onClick={() => setCurrentImageIndex((prev) => (prev + 1) % currentImageSet.length)}
            className="absolute right-3 sm:right-6 top-1/2 transform -translate-y-1/2 z-20 glass-effect rounded-full p-2 sm:p-3 hover:scale-110 transition-all duration-300 group"
            aria-label="Next image"
          >
            <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white group-hover:text-highlight" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
        
        <div className="relative z-20 h-full flex items-center">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="max-w-5xl mx-auto text-center">
              <motion.div 
                className="glass-effect-mobile sm:glass-effect rounded-xl sm:rounded-2xl p-6 sm:p-8 md:p-12"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, ease: "easeOut" }}
              >
                <motion.h1 
                  className="text-4xl sm:text-5xl md:text-7xl font-light mb-4 sm:mb-6 mobile-hero-text"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 1, delay: 0.3 }}
                >
                  <span className="text-gradient block">Beyond</span>
                  <span className="text-gradient block">Cinema</span>
                </motion.h1>
                
                <motion.p 
                  className="text-lg sm:text-xl md:text-2xl text-secondary/90 mb-6 sm:mb-8 leading-relaxed max-w-4xl mx-auto mobile-hero-subtitle"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 1, delay: 0.5 }}
                >
                  The Parallax Pro 8K transcends traditional projection to become the foundation 
                  for extraordinary experiences. From championship golf courses to cockpit simulations, 
                  discover how precision engineering transforms spaces into immersive sanctuaries.
                </motion.p>
                
                <motion.div 
                  className="text-highlight/90 text-xs sm:text-sm uppercase tracking-wider font-medium mobile-hero-tagline"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 1, delay: 0.7 }}
                >
                  Luxury Experiential Technology
                </motion.div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* EXPERIENCE CATEGORIES OVERVIEW */}
      <section className="py-20 luxury-gradient">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto text-center mb-16">
            <motion.h2 
              className="text-4xl md:text-5xl font-light mb-8 text-gradient"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              Three Worlds of Luxury
            </motion.h2>
            <motion.p 
              className="text-xl text-secondary/80 max-w-3xl mx-auto"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
            >
              Each experience showcases the versatility and precision of our flagship 
              Pro 8K projector, transforming spaces into immersive environments that 
              rival professional installations.
            </motion.p>
          </div>
          
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Cinema Experience */}
            <motion.div 
              className="glass-effect rounded-xl p-8 hover:scale-105 transition-transform duration-300 text-center"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="w-20 h-20 bg-gradient-radial from-highlight/30 to-transparent rounded-full mx-auto mb-6 flex items-center justify-center">
                <span className="text-highlight text-3xl">🎬</span>
              </div>
              <h3 className="text-2xl font-semibold mb-4 text-gradient">Premium Cinema</h3>
              <p className="text-secondary/80 mb-6">
                Studio-grade color accuracy and native 8K resolution create theatrical 
                experiences that rival commercial cinemas.
              </p>
              <div className="text-highlight/70 text-sm font-medium">Starting at $35,000</div>
            </motion.div>
            
            {/* Golf Simulation */}
            <motion.div 
              className="glass-effect rounded-xl p-8 hover:scale-105 transition-transform duration-300 text-center"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <div className="w-20 h-20 bg-gradient-radial from-highlight/30 to-transparent rounded-full mx-auto mb-6 flex items-center justify-center">
                <span className="text-highlight text-3xl">⛳</span>
              </div>
              <h3 className="text-2xl font-semibold mb-4 text-gradient">Golf Simulation</h3>
              <p className="text-secondary/80 mb-6">
                Championship courses at 4000+ lumens with precision ball tracking 
                and photorealistic course environments.
              </p>
              <div className="text-highlight/70 text-sm font-medium">Complete systems from $75,000</div>
            </motion.div>
            
            {/* Flight Simulation */}
            <motion.div 
              className="glass-effect rounded-xl p-8 hover:scale-105 transition-transform duration-300 text-center"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <div className="w-20 h-20 bg-gradient-radial from-highlight/30 to-transparent rounded-full mx-auto mb-6 flex items-center justify-center">
                <span className="text-highlight text-3xl">✈️</span>
              </div>
              <h3 className="text-2xl font-semibold mb-4 text-gradient">Flight Simulation</h3>
              <p className="text-secondary/80 mb-6">
                Professional cockpit experiences with multi-projector setups for 
                270-degree immersive flight training.
              </p>
              <div className="text-highlight/70 text-sm font-medium">Cockpit systems from $95,000</div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* GOLF SIMULATION SHOWCASE */}
      <section className="py-20 bg-primary">
        <div className="container mx-auto px-6">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              
              {/* Golf System Visual - appears first on mobile, second on desktop */}
              <motion.div 
                className="relative order-1 lg:order-2"
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <div className="projector-glass-effect rounded-2xl p-8">
                  <div className="aspect-[4/3] bg-gradient-to-br from-green-500/20 to-blue-500/20 rounded-xl overflow-hidden relative">
                    <video 
                      className="w-full h-full object-cover"
                      autoPlay 
                      muted 
                      loop 
                      playsInline
                      preload="metadata"
                    >
                      <source src="/parallaxcinema-golf-simulation.mp4" type="video/mp4" />
                      {/* Fallback for browsers that don't support video */}
                      <div className="w-full h-full flex items-center justify-center">
                        <div className="text-center">
                          <div className="text-6xl text-highlight/40 mb-4">⛳</div>
                          <div className="text-secondary/60 text-sm uppercase tracking-wider">
                            Professional Golf Simulation
                          </div>
                        </div>
                      </div>
                    </video>
                    
                    {/* Overlay gradient for better text readability */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent pointer-events-none"></div>
                  </div>
                </div>
                
                {/* Floating specs */}
                <div className="absolute -bottom-4 -right-4 glass-effect rounded-lg p-4 z-10">
                  <div className="text-highlight text-lg font-light">10+ Courses</div>
                  <div className="text-secondary/60 text-xs uppercase tracking-wider">Included Library</div>
                </div>
              </motion.div>
              
              {/* Golf Text Content - appears second on mobile, first on desktop */}
              <motion.div
                className="order-2 lg:order-1"
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <div className="mb-4">
                  <span className="text-highlight/80 text-sm uppercase tracking-wider">
                    Golf Experience
                  </span>
                </div>
                
                <h2 className="text-4xl md:text-5xl font-light mb-6 text-gradient">
                  Championship Courses at Home
                </h2>
                
                <p className="text-xl text-secondary/90 mb-8 leading-relaxed">
                  Transform your space into a professional golf training facility. Our Pro 8K's 
                  exceptional brightness and color accuracy recreate the world's most prestigious 
                  courses with photorealistic detail that responds to every swing.
                </p>
                
                {/* Golf System Features */}
                <div className="space-y-6 mb-8">
                  <div className="flex items-start">
                    <div className="w-2 h-2 bg-highlight rounded-full mt-3 mr-4 flex-shrink-0"></div>
                    <div>
                      <h3 className="text-lg font-semibold mb-2 text-gradient">Professional Launch Monitor Integration</h3>
                      <p className="text-secondary/80">TrackMan and SkyTrak compatibility for precise ball flight analysis and swing data</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="w-2 h-2 bg-highlight rounded-full mt-3 mr-4 flex-shrink-0"></div>
                    <div>
                      <h3 className="text-lg font-semibold mb-2 text-gradient">4000+ Lumen Performance</h3>
                      <p className="text-secondary/80">Bright, vivid images that maintain clarity even with room lighting for comfortable play</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="w-2 h-2 bg-highlight rounded-full mt-3 mr-4 flex-shrink-0"></div>
                    <div>
                      <h3 className="text-lg font-semibold mb-2 text-gradient">Impact Screen Integration</h3>
                      <p className="text-secondary/80">Acoustically transparent screens designed to withstand direct ball impact</p>
                    </div>
                  </div>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-4">
                  <button 
                    onClick={openFormPopup}
                    className="btn-primary px-6 py-3"
                  >
                    Explore Golf Systems
                  </button>
                  <button 
                    onClick={openFormPopup}
                    className="btn-secondary px-6 py-3"
                  >
                    Schedule Demo
                  </button>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* FLIGHT SIMULATION SHOWCASE */}
      <section className="py-20 luxury-gradient">
        <div className="container mx-auto px-6">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              
              {/* Flight System Visual */}
              <motion.div 
                className="relative"
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <div className="projector-glass-effect rounded-2xl p-8">
                  <div className="aspect-[4/3] bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-xl overflow-hidden relative">
                    <video 
                      className="w-full h-full object-cover"
                      autoPlay 
                      muted 
                      loop 
                      playsInline
                      preload="metadata"
                    >
                      <source src="/parallaxcinema-flight-simulation.mp4" type="video/mp4" />
                      {/* Fallback for browsers that don't support video */}
                      <div className="w-full h-full flex items-center justify-center">
                        <div className="text-center">
                          <div className="text-6xl text-highlight/40 mb-4">✈️</div>
                          <div className="text-secondary/60 text-sm uppercase tracking-wider">
                            Professional Flight Simulation
                          </div>
                        </div>
                      </div>
                    </video>
                    
                    {/* Overlay gradient for better text readability */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent pointer-events-none"></div>
                  </div>
                </div>
                
                {/* Floating specs */}
                <div className="absolute -bottom-4 -left-4 glass-effect rounded-lg p-4 z-10">
                  <div className="text-highlight text-lg font-light">270° FOV</div>
                  <div className="text-secondary/60 text-xs uppercase tracking-wider">Immersive View</div>
                </div>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <div className="mb-4">
                  <span className="text-highlight/80 text-sm uppercase tracking-wider">
                    Flight Experience
                  </span>
                </div>
                
                <h2 className="text-4xl md:text-5xl font-light mb-6 text-gradient">
                  Professional Cockpit Environments
                </h2>
                
                <p className="text-xl text-secondary/90 mb-8 leading-relaxed">
                  Experience the precision of commercial aviation training in your own space. 
                  Multi-projector configurations create wraparound environments that deliver 
                  the most realistic flight simulation experience possible.
                </p>
                
                {/* Flight System Features */}
                <div className="space-y-6 mb-8">
                  <div className="flex items-start">
                    <div className="w-2 h-2 bg-highlight rounded-full mt-3 mr-4 flex-shrink-0"></div>
                    <div>
                      <h3 className="text-lg font-semibold mb-2 text-gradient">Multi-Projector Array</h3>
                      <p className="text-secondary/80">2-3 Pro 8K projectors create seamless 180-270° wraparound displays</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="w-2 h-2 bg-highlight rounded-full mt-3 mr-4 flex-shrink-0"></div>
                    <div>
                      <h3 className="text-lg font-semibold mb-2 text-gradient">Curved Screen Integration</h3>
                      <p className="text-secondary/80">Custom curved projection surfaces for maximum immersion and realistic peripheral vision</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="w-2 h-2 bg-highlight rounded-full mt-3 mr-4 flex-shrink-0"></div>
                    <div>
                      <h3 className="text-lg font-semibold mb-2 text-gradient">Professional Cockpit Hardware</h3>
                      <p className="text-secondary/80">Compatible with commercial-grade flight controls and motion platforms</p>
                    </div>
                  </div>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-4">
                  <button 
                    onClick={openFormPopup}
                    className="btn-primary px-6 py-3"
                  >
                    Explore Flight Systems
                  </button>
                  <button 
                    onClick={openFormPopup}
                    className="btn-secondary px-6 py-3"
                  >
                    Schedule Demo
                  </button>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* TECHNICAL FOUNDATION SECTION */}
      <section className="py-20 bg-primary">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto text-center">
            <motion.h2 
              className="text-4xl md:text-5xl font-light mb-8 text-gradient"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              One Foundation, Infinite Possibilities
            </motion.h2>
            <motion.p 
              className="text-xl text-secondary/80 mb-16 max-w-3xl mx-auto"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
            >
              The Parallax Pro 8K's exceptional specifications make it the perfect 
              foundation for any premium experience you envision.
            </motion.p>
            
            <div className="grid md:grid-cols-4 gap-8">
              <motion.div 
                className="glass-effect rounded-xl p-6 text-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <div className="text-3xl font-light text-highlight mb-4">8K</div>
                <h3 className="text-lg font-semibold mb-2 text-gradient">Native Resolution</h3>
                <p className="text-secondary/80 text-sm">Uncompromising detail for any application</p>
              </motion.div>
              
              <motion.div 
                className="glass-effect rounded-xl p-6 text-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                viewport={{ once: true }}
              >
                <div className="text-3xl font-light text-highlight mb-4">4,000+</div>
                <h3 className="text-lg font-semibold mb-2 text-gradient">Lumens Output</h3>
                <p className="text-secondary/80 text-sm">Bright enough for any lighting condition</p>
              </motion.div>
              
              <motion.div 
                className="glass-effect rounded-xl p-6 text-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
              >
                <div className="text-3xl font-light text-highlight mb-4">1M:1</div>
                <h3 className="text-lg font-semibold mb-2 text-gradient">Contrast Ratio</h3>
                <p className="text-secondary/80 text-sm">Deep blacks and brilliant highlights</p>
              </motion.div>
              
              <motion.div 
                className="glass-effect rounded-xl p-6 text-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                viewport={{ once: true }}
              >
                <div className="text-3xl font-light text-highlight mb-4">DCI-P3</div>
                <h3 className="text-lg font-semibold mb-2 text-gradient">Color Space</h3>
                <p className="text-secondary/80 text-sm">Cinema-grade color accuracy</p>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* EXPERIENCE COMPARISON TABLE */}
      <section className="py-20 luxury-gradient">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <motion.h2 
              className="text-3xl font-light text-center mb-12 text-gradient"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              Experience Comparison
            </motion.h2>
            
            <motion.div 
              className="glass-effect rounded-xl p-8 overflow-x-auto"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <table className="w-full">
                <thead>
                  <tr className="border-b border-secondary/20">
                    <th className="text-left py-4 px-6 text-secondary/80 font-medium">Feature</th>
                    <th className="text-center py-4 px-6 text-gradient font-semibold">Cinema</th>
                    <th className="text-center py-4 px-6 text-gradient font-semibold">Golf Sim</th>
                    <th className="text-center py-4 px-6 text-gradient font-semibold">Flight Sim</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-secondary/10">
                  <tr>
                    <td className="py-4 px-6 text-secondary/80">Recommended Screen Size</td>
                    <td className="py-4 px-6 text-center text-white">100"-150"</td>
                    <td className="py-4 px-6 text-center text-white">120"-150"</td>
                    <td className="py-4 px-6 text-center text-white">180"-270° curved</td>
                  </tr>
                  <tr>
                    <td className="py-4 px-6 text-secondary/80">Optimal Brightness</td>
                    <td className="py-4 px-6 text-center text-white">2,000-3,000 lm</td>
                    <td className="py-4 px-6 text-center text-white">4,000+ lm</td>
                    <td className="py-4 px-6 text-center text-white">3,000-4,000 lm</td>
                  </tr>
                  <tr>
                    <td className="py-4 px-6 text-secondary/80">Projector Configuration</td>
                    <td className="py-4 px-6 text-center text-white">Single Pro 8K</td>
                    <td className="py-4 px-6 text-center text-white">Single Pro 8K</td>
                    <td className="py-4 px-6 text-center text-white">2-3 Pro 8K units</td>
                  </tr>
                  <tr>
                    <td className="py-4 px-6 text-secondary/80">Installation Complexity</td>
                    <td className="py-4 px-6 text-center text-white">Standard</td>
                    <td className="py-4 px-6 text-center text-white">Moderate</td>
                    <td className="py-4 px-6 text-center text-white">Advanced</td>
                  </tr>
                  <tr>
                    <td className="py-4 px-6 text-secondary/80">Total Investment</td>
                    <td className="py-4 px-6 text-center text-white">$35k-$75k</td>
                    <td className="py-4 px-6 text-center text-white">$75k-$150k</td>
                    <td className="py-4 px-6 text-center text-white">$95k-$250k</td>
                  </tr>
                </tbody>
              </table>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CONSULTATION CALL TO ACTION */}
      <section className="py-20 bg-primary">
        <div className="container mx-auto px-6 text-center">
          <div className="max-w-4xl mx-auto">
            <motion.h2 
              className="text-4xl md:text-5xl font-light mb-8 text-gradient"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              Design Your Ultimate Experience
            </motion.h2>
            <motion.p 
              className="text-xl text-secondary/80 mb-12 max-w-3xl mx-auto"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
            >
              Our specialists work with you to design and install the perfect 
              experiential environment for your space, lifestyle, and aspirations.
            </motion.p>
            
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <motion.div 
                className="glass-effect rounded-xl p-8 text-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <h3 className="text-xl font-semibold mb-4 text-gradient">Experience Center</h3>
                <p className="text-secondary/80 mb-6">
                  Visit our showroom to experience all three systems firsthand. 
                  See, touch, and play with the technology before making your investment.
                </p>
                <button 
                  onClick={openFormPopup}
                  className="btn-primary px-8 py-3"
                >
                  Schedule Showroom Visit
                </button>
              </motion.div>
              
              <motion.div 
                className="glass-effect rounded-xl p-8 text-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                viewport={{ once: true }}
              >
                <h3 className="text-xl font-semibold mb-4 text-gradient">In-Home Consultation</h3>
                <p className="text-secondary/80 mb-6">
                  Our design specialists visit your space to create custom solutions 
                  that integrate seamlessly with your architecture and lifestyle.
                </p>
                <button 
                  onClick={openFormPopup}
                  className="btn-primary px-8 py-3"
                >
                  Request Site Visit
                </button>
              </motion.div>
            </div>
            
            <motion.div 
              className="text-secondary/60 text-sm"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              viewport={{ once: true }}
            >
              All consultations include space analysis, custom design proposals, and transparent investment planning
            </motion.div>
          </div>
        </div>
      </section>

      {/* Popup handled globally via FormPopupProvider */}
    </div>
  );
};

export default Experience; 